/* =========================================================================
 * DIVISION II — the championships nobody else is chasing.
 *
 * Brian: "We must solidify d2 and d3. These are the opportunities that fly
 * under the radar that are great units."
 *
 * ⚠️ THE BULK RECORDS WERE ALREADY THERE — 1,272 across the 65 DII/DIII
 * conferences, most of them already dated, and only TWO conferences holding
 * nothing at all. So "solidify" does not mean another thousand rows. Measured
 * against each conference's own 2026-27 schedule, what is actually missing is
 * the UNUSUAL SPORTS — and those are precisely the under-the-radar units:
 * STUNT, esports, flag football, women's bowling, women's wrestling, acro.
 * Nobody is competing for a Peach Belt esports ring.
 *
 * Every entry below was read off the conference's own published championship
 * schedule. Sources are on each block.
 *
 * ⚠️ AND THE BIGGEST FINDING IS A NEGATIVE ONE: MOST DIVISION II FOOTBALL HAS
 * NO CHAMPIONSHIP GAME. The Lone Star, GLVC, NSIC, Gulf South, South Atlantic
 * and RMAC all decide football on STANDINGS — the Gulf South says so in as many
 * words ("awarded to the team(s) with the highest win-loss percentage"). Only
 * the PSAC and the CIAA stage a title game. Recording a "championship game" for
 * the other six would be six phantoms, and it is the single easiest wrong
 * assumption to make here because every FBS conference has one.
 * ========================================================================= */

const SRC = {
  psac: 'https://psacsports.org/sports/2025/10/31/2627champ.aspx?path=gen',
  glvc: 'https://glvcsports.com/tournaments/',
  gsc: 'https://gscsports.org/sports/2026/6/16/2627-champ-schedule.aspx',
  sac: 'https://thesac.com/sports/2026/6/29/champcalendar.aspx',
  rmac: 'https://rmacsports.org/sports/2026/4/30/2026-27-Championships.aspx',
  pbc: 'https://peachbeltconference.org/sports/2026/6/17/26%2027%20championships.aspx',
  ciaa: 'https://theciaa.com/sports/2024/5/23/Championship%20Dates.aspx',
  ssc: 'https://sunshinestateconference.com/sports/2025/7/28/2526championships.aspx',
}

const D = (code, conference, sport, gender, date, source, note = '') => ({
  st: code, state: '', body: conference, sport, gender,
  divisions: ['Championship'],
  source,
  segment: 'Conference', level: 'Conference',
  season: '2027',
  activity: false,
  date,
  note,
})

export const GAPS_D2 = [
  /* ---- THE UNDER-THE-RADAR SPORTS ---------------------------------------
   * Each is a real conference championship with a real winner and a real ring
   * order, and each is the kind of title that never appears on a competitor's
   * board because it is not a sport anyone thinks to look for. */

  /* ESPORTS — the Peach Belt runs TWO, a fall and a spring championship, each
   * with its own date and site. Not an exhibition: a branded conference title. */
  D('PBC', 'Peach Belt Conference', 'Esports', '', '2026-11-14', SRC.pbc,
    'Fall championship — the Peach Belt runs a separate spring esports championship as well.'),

  /* STUNT — an NCAA emerging sport for women, and a genuine conference title in
   * both the GLVC and the Lone Star. It is NOT cheer and it is NOT dance; a
   * record filed under either would be the wrong championship. */
  D('GLVC', 'Great Lakes Valley Conference', 'STUNT', 'Women', '2027-04-03', SRC.glvc,
    'An NCAA emerging sport — distinct from cheer and from dance.'),

  /* FLAG FOOTBALL — the CIAA crowns it in APRIL, entirely separately from its
   * tackle football championship in November. Two football champions, five
   * months apart, and folding them together would lose one of them. */
  D('CIAA', 'Central Intercollegiate Athletic Association', 'Flag Football', 'Women', '2027-04-09', SRC.ciaa,
    'A separate championship from the CIAA tackle football title game in November.'),

  /* WOMEN'S BOWLING — a real NCAA championship sport, crowned by both the GLVC
   * and the CIAA. */
  D('GLVC', 'Great Lakes Valley Conference', 'Bowling', 'Women', '2027-03-15', SRC.glvc),
  D('CIAA', 'Central Intercollegiate Athletic Association', 'Bowling', 'Women', '2027-03-19', SRC.ciaa),

  /* ⚠️ WOMEN'S WRESTLING HAS A TOURNAMENT; MEN'S WRESTLING DOES NOT. The RMAC
   * schedule shows a women's wrestling championship at Air Force on 30 January
   * and an explicit dash for men's — men's is decided on standings. A rule that
   * assumed both genders share a format would invent a men's tournament. */
  D('RMAC', 'Rocky Mountain Athletic Conference', 'Wrestling', 'Women', '2027-01-30', SRC.rmac,
    'The RMAC stages a women\'s wrestling championship; MEN\'S wrestling is decided on standings with no tournament.'),

  /* MEN'S VOLLEYBALL — the GLVC crowns it. Note this is exactly the sport the
   * Big Ten and ACC do NOT crown (their programs play in the EIVA/MIVA), which
   * is what made those two phantoms so easy to believe. */
  D('GLVC', 'Great Lakes Valley Conference', 'Volleyball', 'Men', '2027-04-24', SRC.glvc),

  /* BEACH VOLLEYBALL — South Atlantic and Sunshine State both crown it. */
  D('SAC', 'South Atlantic Conference', 'Beach Volleyball', 'Women', '2027-04-09', SRC.sac),

  /* MEN'S-ONLY LACROSSE. The Peach Belt sponsors men's lacrosse and NOT
   * women's — the reverse of the more common pattern, and worth stating so
   * nobody "completes the pair". */
  D('PBC', 'Peach Belt Conference', 'Lacrosse', 'Men', '2027-04-27', SRC.pbc,
    'The Peach Belt sponsors MEN\'S lacrosse only — there is no women\'s championship.'),

  /* WOMEN'S ROWING — Sunshine State. */
  D('SSC', 'Sunshine State Conference', 'Rowing', 'Women', '2027-04-24', SRC.ssc,
    '⚠️ Dated from the 2025-26 schedule — the SSC had not published 2026-27 at the time of reading.'),

  /* FIELD HOCKEY — PSAC and South Atlantic. */
  D('PSAC', 'Pennsylvania State Athletic Conference', 'Field Hockey', 'Women', '2026-11-10', SRC.psac),
  D('SAC', 'South Atlantic Conference', 'Field Hockey', 'Women', '2026-11-09', SRC.sac),
]

/* =========================================================================
 * ⚠️ WHAT THESE CONFERENCES DO **NOT** CROWN — recorded so it is not guessed.
 *
 * Read off the same schedules. Each of these is a championship somebody would
 * reasonably assume exists, and does not.
 * ========================================================================= */
export const D2_NOT_SPONSORED = {
  /* Football decided on STANDINGS, no title game. Six of the ten conferences
   * read. Only the PSAC (division champs meet 14 Nov) and the CIAA (a fixed
   * host in Durham) actually play for it. */
  standingsOnlyFootball: [
    'Lone Star Conference', 'Great Lakes Valley Conference',
    'Northern Sun Intercollegiate Conference', 'Gulf South Conference',
    'South Atlantic Conference', 'Rocky Mountain Athletic Conference',
  ],
  /* ⚠️ THE SUNSHINE STATE CONFERENCE HOLDS NO TOURNAMENT AT ALL in three full
   * team sports — volleyball, softball and baseball. The regular-season
   * champion takes the automatic NCAA bid. A "SSC Baseball Tournament" record
   * is a phantom, and an undated baseball record there is CORRECT rather than
   * missing a date. */
  noTournament: {
    'Sunshine State Conference': ['Volleyball', 'Softball', 'Baseball'],
    'Rocky Mountain Athletic Conference': ['Wrestling (men)'],
  },
  /* Sports a conference simply does not sponsor, where a neighbour does. */
  notSponsored: {
    'Central Intercollegiate Athletic Association': ['Baseball', 'Soccer', 'Tennis (men)', 'Lacrosse', 'Swimming & Diving', 'Wrestling'],
    'Peach Belt Conference': ['Football', 'Lacrosse (women)'],
    'Sunshine State Conference': ['Football', 'Indoor Track & Field'],
    'Northern Sun Intercollegiate Conference': ['Soccer (men)', 'Tennis (men)'],
    'Pennsylvania State Athletic Conference': ['Lacrosse (men)', 'Volleyball (men)', 'Wrestling'],
    'Rocky Mountain Athletic Conference': ['Tennis'],
  },
}
